import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useGetOrgId, useQuoteListOrgId } from 'src/api/helpers/getOrgId'
import { paths } from 'src/routes/paths'
import { PageContainer } from 'src/shared/components/PageContainer'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { RequestsList } from './components/RequestsList'
import {
  Boxes,
  Clock3,
  FilePenLine,
  FileText,
  List,
  PackageCheck,
  Plus,
} from 'lucide-react'
import { EmptyState } from 'src/shared/components/EmptyState'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import {
  BUILDING_QUOTE_STATUS,
  DELIVERED_STATUS,
  QUOTE_STATUS_OPTIONS,
  isDraftQuoteStatus,
} from 'src/shared/consts/quoteStatus'
import { DeliveredOrdersProductGrid } from './components/DeliveredOrdersProductGrid'
import { RequestsAdminFilters } from './components/RequestsAdminFilters'
import { TabNavigation, Tab } from 'src/shared/components/TabNavigation'
import {
  DEFAULT_REQUEST_SEGMENT,
  LEGACY_REQUEST_LIST_QUERY_KEY,
  REQUEST_LIST_QUERY_KEY,
  REQUEST_SEGMENT_QUERY_KEY,
  type RequestListTab,
  type RequestSegment,
  buildRequestsListSearch,
  parseRequestListTab,
  parseRequestSegment,
} from './requestNavParams'

type RequestsPageProps = {
  embedded?: boolean
  /** Override list → detail navigation base (default `/app`) */
  detailBasePath?: string
  /** Override detail segment (default `/requests`) */
  detailPath?: string
}

export const RequestsPage = (props: RequestsPageProps = {}) => {
  const {
    embedded = false,
    detailBasePath = paths.myAccount,
    detailPath = paths.requests,
  } = props
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { org_id: routeOrgId } = useParams<{ org_id?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const listSegment = parseRequestSegment(
    searchParams.get(REQUEST_SEGMENT_QUERY_KEY)
  )
  const requestListTab = parseRequestListTab(
    searchParams.get(REQUEST_LIST_QUERY_KEY),
    searchParams.get(LEGACY_REQUEST_LIST_QUERY_KEY)
  )

  const [selectedQuote, setSelectedQuote] = useState('')
  const [selectedOrg, setSelectedOrg] = useState('')
  const [hasItemsOnly, setHasItemsOnly] = useState(true)
  const [excludeAnonOrgs, setExcludeAnonOrgs] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all')
  const [orgSearchInput, setOrgSearchInput] = useState('')
  const debouncedOrgSearch = useDebounce(orgSearchInput, 300)

  const isAdmin = useIsAdmin()
  const org_id = useGetOrgId()
  const quoteListOrgId = useQuoteListOrgId()
  const isScopedOrgRoute = Boolean(routeOrgId && isAdmin)

  const { organizationById, useSearchOrganizations } = useOrgsApi({ org_id })
  const { data: searchedOrgs } = useSearchOrganizations({
    search: debouncedOrgSearch,
    limit: 20,
  })
  const { quotes, createQuote, isLoadingQuotes } = useQuoteApi({
    org_id: quoteListOrgId,
    list: {
      limit: 100,
      // Needed so cards can flip `2_approved_quote` → "Awaiting approval" when proofs exist
      includeProofs: true,
      countMode: 'none',
      ...(!quoteListOrgId
        ? { excludeStatuses: [DELIVERED_STATUS] }
        : {}),
    },
  })

  const allQuotes = quotes?.data || []

  const quotesBySegment = useMemo(() => {
    if (listSegment === 'all') {
      return allQuotes
    }
    if (listSegment === 'active') {
      return allQuotes.filter(
        (q) => q.status !== DELIVERED_STATUS && !isDraftQuoteStatus(q.status)
      )
    }
    if (listSegment === 'delivered') {
      return allQuotes.filter((q) => q.status === DELIVERED_STATUS)
    }
    return allQuotes.filter((q) => isDraftQuoteStatus(q.status))
  }, [allQuotes, listSegment])

  const quoteOptions = useMemo(() => {
    const sorted = [...quotesBySegment].sort(
      (a, b) =>
        new Date(b.created_at ?? 0).getTime() -
        new Date(a.created_at ?? 0).getTime()
    )
    return sorted.map((q) => ({
      value: String(q.id),
      label: [q.name, q.public_id].filter(Boolean).join(' — '),
    }))
  }, [quotesBySegment])

  const orgOptions = useMemo(
    () =>
      (searchedOrgs?.data ?? [])
        .filter((org) => org.name !== 'Anon')
        .map((org) => ({ value: org.id, label: org.name })),
    [searchedOrgs?.data]
  )

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('All statuses', 'Alla statusar') },
      ...QUOTE_STATUS_OPTIONS.filter(
        (opt) => opt.value !== DELIVERED_STATUS
      ).map((opt) => ({
        value: opt.value,
        label: t(opt.label),
      })),
    ],
    [t]
  )

  const filteredQuotes = useMemo(() => {
    const sortByCreated = (list: typeof quotesBySegment) =>
      [...list].sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
      )

    if (!isAdmin || isScopedOrgRoute) {
      return sortByCreated(quotesBySegment)
    }

    let next = quotesBySegment

    if (selectedQuote) {
      next = next.filter((q) => String(q.id) === selectedQuote)
    }
    if (selectedOrg) {
      next = next.filter((q) => q.org_id === selectedOrg)
    }
    if (hasItemsOnly) {
      next = next.filter((q) => (q.quote_item?.length ?? 0) > 0)
    }
    if (excludeAnonOrgs) {
      next = next.filter((q) => q.organization?.name !== 'Anon')
    }
    if (statusFilter !== 'all' && listSegment !== 'drafts') {
      if (statusFilter === BUILDING_QUOTE_STATUS) {
        next = next.filter((q) => isDraftQuoteStatus(q.status))
      } else {
        next = next.filter((q) => q.status === statusFilter)
      }
    }

    return sortByCreated(next)
  }, [
    quotesBySegment,
    isAdmin,
    isScopedOrgRoute,
    selectedQuote,
    selectedOrg,
    hasItemsOnly,
    excludeAnonOrgs,
    statusFilter,
    listSegment,
  ])

  const hasActiveFilters = Boolean(
    selectedQuote ||
      selectedOrg ||
      !hasItemsOnly ||
      !excludeAnonOrgs ||
      (listSegment !== 'drafts' && statusFilter !== 'all')
  )

  const handleReset = () => {
    setSelectedQuote('')
    setSelectedOrg('')
    setHasItemsOnly(true)
    setExcludeAnonOrgs(true)
    setStatusFilter('all')
  }

  const setSegment = (next: RequestSegment) => {
    const p = new URLSearchParams(searchParams)
    if (next === DEFAULT_REQUEST_SEGMENT) {
      p.delete(REQUEST_SEGMENT_QUERY_KEY)
    } else {
      p.set(REQUEST_SEGMENT_QUERY_KEY, next)
    }
    if (next !== 'delivered') {
      p.delete(REQUEST_LIST_QUERY_KEY)
      p.delete(LEGACY_REQUEST_LIST_QUERY_KEY)
    }
    setSearchParams(p, { replace: true })
  }

  const setDeliveredListTab = (next: RequestListTab) => {
    const p = new URLSearchParams(searchParams)
    p.set(REQUEST_SEGMENT_QUERY_KEY, 'delivered')
    p.delete(LEGACY_REQUEST_LIST_QUERY_KEY)
    if (next === 'requests') {
      p.delete(REQUEST_LIST_QUERY_KEY)
    } else {
      p.set(REQUEST_LIST_QUERY_KEY, next)
    }
    setSearchParams(p, { replace: true })
  }

  const handleCreateQuote = async () => {
    const nextQuoteNumber = allQuotes.length + 1
    const quoteName = `${t('Request', 'Förfrågan')} #${nextQuoteNumber}`

    const res = await createQuote.mutateAsync({
      name: quoteName,
      org_id: organizationById?.data?.id,
    })

    if (res?.public_id) {
      navigate(`${detailBasePath}${detailPath}/${res.public_id}`)
    }
  }

  const shell = (children: ReactNode) =>
    embedded ? (
      <Column className="gap-4">{children}</Column>
    ) : (
      <PageContainer className="gap-4">{children}</PageContainer>
    )

  const listSearch = buildRequestsListSearch({
    segment: listSegment,
    requestList: requestListTab,
  })

  const segmentTabs: Tab[] = useMemo(
    () => [
      {
        id: 'all',
        label: t('All', 'Alla'),
        icon: <List size={16} strokeWidth={2} className="shrink-0" />,
        badge: allQuotes.length,
      },
      {
        id: 'active',
        label: t('Active', 'Aktiva'),
        icon: <Clock3 size={16} strokeWidth={2} className="shrink-0" />,
        badge: allQuotes.filter(
          (q) => q.status !== DELIVERED_STATUS && !isDraftQuoteStatus(q.status)
        ).length,
      },
      {
        id: 'drafts',
        label: t('Drafts', 'Utkast'),
        icon: <FilePenLine size={16} strokeWidth={2} className="shrink-0" />,
        badge: allQuotes.filter((q) => isDraftQuoteStatus(q.status)).length,
      },
      {
        id: 'delivered',
        label: t('Delivered', 'Levererade'),
        icon: <PackageCheck size={16} strokeWidth={2} className="shrink-0" />,
        badge: allQuotes.filter((q) => q.status === DELIVERED_STATUS).length,
      },
    ],
    [allQuotes, t]
  )

  const deliveredSubTabs: Tab[] = useMemo(
    () => [
      {
        id: 'requests',
        label: t('Requests', 'Förfrågningar'),
        icon: <FileText size={16} strokeWidth={2} className="shrink-0" />,
      },
      {
        id: 'items',
        label: t('Items', 'Artiklar'),
        icon: <Boxes size={16} strokeWidth={2} className="shrink-0" />,
      },
    ],
    [t]
  )

  if (isLoadingQuotes) {
    return <FullScreenLoader />
  }

  if (!quotes?.data || quotes?.data?.length === 0) {
    return shell(
      <>
        <Column className="gap-4">
          <Row className="items-center gap-2 flex-wrap">
            <FileText size={20} strokeWidth={2} className="shrink-0" />
            <h1>{t('Requests', 'Förfrågningar')}</h1>

            {!isAdmin && (
              <Button
                size="default"
                className="ml-auto"
                onClick={handleCreateQuote}
              >
                <Plus size={16} strokeWidth={2} className="mr-2 shrink-0" />
                {t('New request', 'Ny förfrågan')}
              </Button>
            )}
          </Row>

          <EmptyState
            icon={<FileText size={36} strokeWidth={2} className="shrink-0" />}
            title={t('No requests yet', 'Inga förfrågningar ännu')}
            description={t(
              'Create a new request to get started',
              'Skapa en ny förfrågan för att komma igång'
            )}
          />
        </Column>
      </>
    )
  }

  const showAdminFilterRow =
    isAdmin && !isScopedOrgRoute && requestListTab === 'requests'

  return shell(
    <>
      <Column>
        <Row className="items-center gap-2 flex-wrap">
          <FileText size={20} strokeWidth={2} className="shrink-0" />
          <h1>{t('Requests', 'Förfrågningar')}</h1>

          {!isAdmin &&
            (listSegment === 'all' ||
              listSegment === 'active' ||
              listSegment === 'drafts') && (
            <Button className="ml-auto" onClick={handleCreateQuote}>
              <Plus size={16} strokeWidth={2} className="mr-2 shrink-0" />
              {t('New request', 'Ny förfrågan')}
            </Button>
          )}
        </Row>

        <Column className="gap-2 mt-4">
          <TabNavigation
            tabs={segmentTabs}
            activeTab={listSegment}
            onTabChange={(id) => setSegment(id as RequestSegment)}
          />

          {listSegment === 'delivered' && (
            <TabNavigation
              tabs={deliveredSubTabs}
              activeTab={requestListTab}
              onTabChange={(id) => setDeliveredListTab(id as RequestListTab)}
            />
          )}
        </Column>

        {showAdminFilterRow && (
          <RequestsAdminFilters
            listSegment={listSegment}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            selectedQuote={selectedQuote}
            setSelectedQuote={setSelectedQuote}
            selectedOrg={selectedOrg}
            setSelectedOrg={setSelectedOrg}
            orgSearchInput={orgSearchInput}
            setOrgSearchInput={setOrgSearchInput}
            hasItemsOnly={hasItemsOnly}
            setHasItemsOnly={setHasItemsOnly}
            excludeAnonOrgs={excludeAnonOrgs}
            setExcludeAnonOrgs={setExcludeAnonOrgs}
            hasActiveFilters={hasActiveFilters}
            onReset={handleReset}
            quoteOptions={quoteOptions}
            orgOptions={orgOptions}
            statusOptions={statusOptions}
          />
        )}
      </Column>

      {listSegment === 'delivered' && requestListTab === 'items' ? (
        <DeliveredOrdersProductGrid embedded />
      ) : filteredQuotes.length === 0 ? (
        <EmptyState
          icon={<FileText size={36} strokeWidth={2} className="shrink-0" />}
          title={t('No requests in this view', 'Inga förfrågningar i denna vy')}
          description={t(
            'Try another tab or adjust your filters.',
            'Prova en annan flik eller justera dina filter.'
          )}
        />
      ) : (
        <RequestsList
          quotes={filteredQuotes}
          listSearch={listSearch}
          detailPath={detailPath}
          basePath={detailBasePath}
        />
      )}
    </>
  )
}

/** @deprecated Use RequestsPage */
export const OrdersPage = RequestsPage
